import apiClient from './client';

// ========================
// Types
// ========================

export interface LoginPayload {
  email: string;
  password: string;
}

export interface RegisterPayload {
  companyName: string;
  contactPerson: string;
  email: string;
  phone: string;
  password: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface AuthResponse {
  user: User;
  accessToken?: string;
  refreshToken?: string;
  tokens?: AuthTokens;
}

export interface User {
  id: string;
  email: string;
  role: string;
  companyName?: string;
  contactPerson?: string;
  phone?: string;
  avatar?: string;
  clientId?: string;
}

export interface TicketsListParams {
  page?: number;
  limit?: number;
  status?: string;
  priority?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface CreateTicketPayload {
  subject: string;
  description: string;
  category: string;
  priority: string;
  clubId?: string;
  deviceId?: string;
  softwareVersion?: string;
}

export interface AddCommentPayload {
  content: string;
  isInternal?: boolean;
  // ID'шники файлов, заранее залитых через POST /tickets/:id/attachments.
  // Сервер привяжет их commentId к создаваемому комментарию, и они
  // отрендерятся inline вместе с текстом сообщения. Без них файлы
  // оказываются прицеплены к тикету целиком и не показываются рядом с
  // конкретным сообщением.
  attachmentIds?: string[];
}

export interface RateTicketPayload {
  rating: number;
  comment?: string;
}

export interface Ticket {
  id: string;
  number: number;
  subject: string;
  description: string;
  status: string;
  priority: string;
  category: string;
  clubId?: string;
  clubName?: string;
  deviceId?: string;
  deviceName?: string;
  softwareVersion?: string;
  assignee?: {
    id: string;
    name: string;
    avatar?: string;
  };
  rating?: number;
  ratingComment?: string;
  slaDeadline?: string;
  createdAt: string;
  updatedAt: string;
  closedAt?: string;
  resolutionNote?: string;
  resolutionCategory?: string;
  attachments?: Attachment[];
}

export interface TicketComment {
  id: string;
  content: string;
  isInternal: boolean;
  /** Where the comment was posted from: 'client' = client portal,
   *  'staff' = admin portal. Used to drive messenger-style alignment
   *  even when the same user account writes on both sides. */
  origin?: 'client' | 'staff' | null;
  author: {
    id: string;
    name: string;
    avatar?: string;
    role: string;
    /** Автор — service-user (support-greeting@system.local и подобные).
     *  В UI такие сообщения рендерятся как центральная system-плашка,
     *  а не bubble от «оператора» — иначе автоответы визуально
     *  сливаются с живыми ответами. */
    isSystem?: boolean;
  };
  attachments: Attachment[];
  createdAt: string;
  /** Когда сообщение было последний раз изменено; для пометки «(изм.)». */
  updatedAt?: string;
}

export interface Attachment {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  url: string;
}

export interface KnowledgeCategory {
  id: string;
  name: string;
  slug: string;
  description: string;
  icon?: string;
  articleCount: number;
}

export interface ArticlesListParams {
  page?: number;
  limit?: number;
  categoryId?: string;
  search?: string;
}

export type DemoAction =
  | { type: 'move'; to: [number, number]; duration?: number }
  | { type: 'click'; at: [number, number] }
  | { type: 'wait'; duration: number }
  | { type: 'type'; at: [number, number]; text: string }
  | { type: 'highlight'; box: [number, number, number, number]; duration?: number };

export interface DemoStep {
  imageUrl: string;
  caption?: string;
  actions: DemoAction[];
}

export interface UploadedKnowledgeMedia {
  url: string;
  fileName: string;
  fileSize?: number;
  mimeType?: string;
}

export interface CreateKnowledgeArticlePayload {
  title: string;
  slug: string;
  content: string;
  categoryId: string;
  excerpt?: string;
  type?: 'public' | 'internal' | 'client_specific';
  clientId?: string;
  isPublished?: boolean;
  sortOrder?: number;
  tags?: string[];
  demoSteps?: DemoStep[] | null;
  clips?: unknown[] | null;
}

export type UpdateKnowledgeArticlePayload = Partial<CreateKnowledgeArticlePayload>;

export interface Article {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  category: KnowledgeCategory;
  author: {
    id: string;
    name: string;
  };
  viewCount: number;
  helpfulCount: number;
  createdAt: string;
  updatedAt: string;
  type?: 'public' | 'internal' | 'client_specific';
  isPublished?: boolean;
  attachments?: Attachment[];
  relatedArticles?: Article[];
  demoSteps?: DemoStep[] | null;
  tags?: string[];
  // Часть Б: бэк помечает статьи, подходящие клубу клиента (по паспорту).
  matchedForClub?: boolean;
  matchScore?: number;
  matchReason?: 'tags' | 'semantic' | null;
}

export interface Club {
  id: string;
  name: string;
  address: string;
  deviceCount: number;
  devices?: Device[];
}

export interface Device {
  id: string;
  name: string;
  type: string;
  serialNumber?: string;
  status: string;
}

export interface ClientProfile {
  id: string;
  companyName: string;
  contactPerson: string;
  email: string;
  phone: string;
  tariff?: string;
  tariffExpiry?: string;
  clubs: Club[];
  customData?: Record<string, string>;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface UpdateProfilePayload {
  contactPerson?: string;
  phone?: string;
  email?: string;
}

// ========================
// Helpers
// ========================

/**
 * Unwrap backend response format: { success: true, data: ... }
 * Returns the `data` field from the response body.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function unwrap<T = any>(response: { data: any }): T {
  const body = response.data;
  // Backend wraps in { success, data } — unwrap it
  if (body && typeof body === 'object' && 'success' in body && 'data' in body) {
    return body.data as T;
  }
  return body as T;
}

/**
 * Build PaginatedResponse from backend list response.
 * Backend may return { items, total, page, limit } or an array directly.
 */
function toPaginated<T>(raw: unknown, fallbackPage = 1, fallbackLimit = 15): PaginatedResponse<T> {
  // If backend returns { items, total, page, limit }
  if (raw && typeof raw === 'object' && 'items' in (raw as Record<string, unknown>)) {
    const obj = raw as { items: T[]; total: number; page: number; limit: number };
    const total = obj.total || obj.items.length;
    const limit = obj.limit || fallbackLimit;
    const page = obj.page || fallbackPage;
    return {
      data: obj.items,
      meta: {
        total,
        page,
        limit,
        totalPages: Math.max(1, Math.ceil(total / limit)),
      },
    };
  }
  // If backend returns { data: [...], total, ... }
  if (raw && typeof raw === 'object' && 'data' in (raw as Record<string, unknown>) && Array.isArray((raw as any).data)) {
    const obj = raw as { data: T[]; total?: number; page?: number; limit?: number };
    const total = obj.total || obj.data.length;
    const limit = obj.limit || fallbackLimit;
    const page = obj.page || fallbackPage;
    return {
      data: obj.data,
      meta: {
        total,
        page,
        limit,
        totalPages: Math.max(1, Math.ceil(total / limit)),
      },
    };
  }
  // If backend returns plain array
  if (Array.isArray(raw)) {
    return {
      data: raw as T[],
      meta: {
        total: raw.length,
        page: fallbackPage,
        limit: fallbackLimit,
        totalPages: 1,
      },
    };
  }
  // Fallback
  return {
    data: [],
    meta: { total: 0, page: fallbackPage, limit: fallbackLimit, totalPages: 1 },
  };
}

/**
 * Normalize a ticket from the backend into the Ticket shape the UI expects.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeTicket(raw: any): Ticket {
  if (!raw) return raw;
  return {
    id: raw.id,
    number: raw.number,
    subject: raw.subject,
    description: raw.description,
    status: raw.status,
    priority: raw.priority,
    category: raw.category,
    clubId: raw.clubId,
    clubName: raw.clubName || raw.club?.name,
    deviceId: raw.deviceId,
    deviceName: raw.deviceName || raw.device?.name,
    softwareVersion: raw.softwareVersion,
    assignee: raw.assignee
      ? {
          id: raw.assignee.id,
          name: raw.assignee.name || `${raw.assignee.firstName || ''} ${raw.assignee.lastName || ''}`.trim(),
          avatar: raw.assignee.avatar,
        }
      : undefined,
    rating: raw.rating,
    ratingComment: raw.ratingComment,
    slaDeadline: raw.slaDeadline,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    closedAt: raw.closedAt,
    resolutionNote: raw.resolutionNote,
    resolutionCategory: raw.resolutionCategory,
    attachments: (raw.attachments || []).map((att: any) => ({
      id: att.id,
      filename: att.fileName || att.originalName || att.filename || 'file',
      originalName: att.fileName || att.originalName || att.filename || 'Файл',
      mimeType: att.mimeType || 'application/octet-stream',
      size: att.fileSize ?? att.size ?? 0,
      url: att.storageKey
        ? att.storageKey.replace(/^\/app/, '')
        : (att.url || ''),
    })),
  };
}

/**
 * Normalize a comment from the backend into the TicketComment shape.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeComment(raw: any): TicketComment {
  if (!raw) return raw;
  const role = raw.author?.role || 'client';
  // Сотрудник в чате клиента всегда показывается как «Оператор #N» —
  // настоящие имя/фамилию клиент не должен видеть.
  const opNum = raw.author?.operatorNumber;
  const isStaff = role !== 'client';
  const fallbackName = `${raw.author?.firstName || ''} ${raw.author?.lastName || ''}`.trim();
  const name = isStaff
    ? (opNum ? `Оператор #${opNum}` : 'Оператор поддержки')
    : (raw.author?.name || fallbackName || 'Клиент');
  return {
    id: raw.id,
    content: raw.content,
    isInternal: raw.isInternal || false,
    origin: raw.origin ?? null,
    author: {
      id: raw.author?.id || raw.authorId,
      name,
      avatar: raw.author?.avatar,
      role,
      isSystem: !!raw.author?.isSystem,
    },
    attachments: (raw.attachments || []).map((att: any) => ({
      id: att.id,
      filename: att.fileName || att.originalName || att.filename || 'file',
      originalName: att.fileName || att.originalName || att.filename || 'Файл',
      mimeType: att.mimeType || 'application/octet-stream',
      size: att.fileSize ?? att.size ?? 0,
      url: att.storageKey
        ? att.storageKey.replace(/^\/app/, '')
        : (att.url || ''),
    })),
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeAuthUser(raw: any): User {
  const contactPerson =
    raw.contactPerson ||
    raw.contactName ||
    raw.client?.contactName ||
    [raw.firstName, raw.lastName].filter(Boolean).join(' ').trim() ||
    undefined;

  return {
    id: raw.id,
    email: raw.email || raw.contactEmail || raw.user?.email,
    role: raw.role || raw.user?.role || 'client',
    companyName: raw.companyName || raw.client?.companyName,
    contactPerson,
    phone: raw.phone || raw.contactPhone || raw.client?.contactPhone || raw.user?.phone,
    avatar: raw.avatar || raw.avatarUrl || raw.user?.avatar || raw.user?.avatarUrl,
    clientId: raw.clientId || raw.client?.id,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeClientProfile(raw: any, fallbackId?: string): ClientProfile {
  return {
    id: raw.id || fallbackId || '',
    companyName: raw.companyName,
    contactPerson:
      raw.contactPerson ||
      raw.contactName ||
      [raw.user?.firstName, raw.user?.lastName].filter(Boolean).join(' ').trim(),
    email: raw.email || raw.contactEmail || raw.user?.email,
    phone: raw.phone || raw.contactPhone || raw.user?.phone,
    tariff: raw.tariff,
    tariffExpiry: raw.tariffExpiry || raw.tariffExpiresAt,
    clubs: raw.clubs || [],
    customData: raw.customData || {},
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeKnowledgeCategory(raw: any): KnowledgeCategory {
  return {
    id: raw.id,
    name: raw.name,
    slug: raw.slug,
    description: raw.description,
    icon: raw.icon,
    articleCount: raw.articleCount ?? raw._count?.articles ?? 0,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeKnowledgeArticle(raw: any): Article {
  return {
    ...raw,
    category: raw.category ? normalizeKnowledgeCategory(raw.category) : raw.category,
    author: raw.author
      ? {
          id: raw.author.id,
          name: raw.author.name || `${raw.author.firstName || ''} ${raw.author.lastName || ''}`.trim(),
        }
      : raw.author,
    relatedArticles: Array.isArray(raw.relatedArticles)
      ? raw.relatedArticles.map(normalizeKnowledgeArticle)
      : raw.relatedArticles,
  };
}

// ========================
// Auth API
// ========================

export const authApi = {
  login: async (payload: LoginPayload): Promise<AuthResponse> => {
    const resp = await apiClient.post('/auth/login', payload);
    const data = unwrap<any>(resp);
    return {
      user: normalizeAuthUser(data.user),
      accessToken: data.accessToken || data.tokens?.accessToken,
      refreshToken: data.refreshToken || data.tokens?.refreshToken,
    };
  },

  register: async (payload: RegisterPayload): Promise<AuthResponse> => {
    // Map frontend fields to backend schema
    const nameParts = payload.contactPerson.trim().split(/\s+/);
    const body = {
      email: payload.email,
      password: payload.password,
      firstName: nameParts[0] || payload.contactPerson,
      lastName: nameParts.slice(1).join(' ') || '-',
      companyName: payload.companyName,
      contactPhone: payload.phone,
      phone: payload.phone,
    };
    const resp = await apiClient.post('/auth/register', body);
    const data = unwrap<any>(resp);
    return {
      user: normalizeAuthUser(data.user),
      tokens: { accessToken: data.accessToken, refreshToken: data.refreshToken },
    };
  },

  refreshToken: async (refreshToken: string) => {
    const resp = await apiClient.post('/auth/refresh', { refreshToken });
    const data = unwrap<AuthTokens>(resp);
    return data;
  },

  getMe: async (): Promise<User> => {
    const resp = await apiClient.get('/auth/me');
    return normalizeAuthUser(unwrap<any>(resp));
  },
};

// ========================
// Tickets API
// ========================

export const ticketsApi = {
  list: async (params?: TicketsListParams): Promise<PaginatedResponse<Ticket>> => {
    const resp = await apiClient.get('/tickets', { params });
    const raw = unwrap(resp);
    const paginated = toPaginated<Ticket>(raw, params?.page || 1, params?.limit || 15);
    paginated.data = paginated.data.map(normalizeTicket);
    return paginated;
  },

  getById: async (id: string): Promise<{ ticket: Ticket; comments: TicketComment[] }> => {
    const resp = await apiClient.get(`/tickets/${id}`);
    const raw = unwrap(resp);

    // Backend may return ticket with embedded comments, or just the ticket
    const ticket = normalizeTicket(raw.ticket || raw);
    const comments: TicketComment[] = (raw.comments || raw.ticket?.comments || []).map(normalizeComment);

    return { ticket, comments };
  },

  create: async (payload: CreateTicketPayload): Promise<Ticket> => {
    const resp = await apiClient.post('/tickets', payload);
    const raw = unwrap(resp);
    return normalizeTicket(raw);
  },

  addComment: async (id: string, payload: AddCommentPayload): Promise<TicketComment> => {
    const resp = await apiClient.post(`/tickets/${id}/comments`, { ...payload, origin: 'client' });
    const raw = unwrap(resp);
    return normalizeComment(raw);
  },

  updateComment: async (ticketId: string, commentId: string, content: string): Promise<TicketComment> => {
    const resp = await apiClient.patch(`/tickets/${ticketId}/comments/${commentId}`, { content });
    const raw = unwrap(resp);
    return normalizeComment(raw);
  },

  deleteComment: async (ticketId: string, commentId: string): Promise<void> => {
    await apiClient.delete(`/tickets/${ticketId}/comments/${commentId}`);
  },

  deleteAttachment: async (ticketId: string, attachmentId: string): Promise<void> => {
    await apiClient.delete(`/tickets/${ticketId}/attachments/${attachmentId}`);
  },

  uploadAttachment: async (id: string, file: File): Promise<Attachment> => {
    const formData = new FormData();
    formData.append('files', file);

    // НЕ выставляем Content-Type руками. axios 1.x при FormData сам
    // подставит `multipart/form-data; boundary=...`. Раньше мы прибивали
    // `'Content-Type': 'multipart/form-data'` БЕЗ boundary → multer
    // на сервере не мог найти разделитель и отвечал 400 («вложение не
    // прикрепляется на хелп-сайте»).
    const resp = await apiClient.post(`/tickets/${id}/attachments`, formData);
    // Бэк возвращает массив (route = upload.array). unwrap<Attachment>
    // давал массив под видом одного объекта → att.id = undefined →
    // addComment получал attachmentIds=[null] → zod 400.
    const data = unwrap<Attachment | Attachment[]>(resp);
    const att = Array.isArray(data) ? data[0] : data;
    if (!att) throw new Error('Upload returned empty attachment payload');
    return att;
  },

  rate: async (id: string, payload: RateTicketPayload): Promise<Ticket> => {
    const resp = await apiClient.post(`/tickets/${id}/rate`, payload);
    const raw = unwrap(resp);
    return normalizeTicket(raw);
  },

  // Клиент сам отмечает обращение завершённым. После этого появится форма оценки.
  clientResolve: async (id: string): Promise<Ticket> => {
    const resp = await apiClient.post(`/tickets/${id}/client-resolve`);
    const raw = unwrap(resp);
    return normalizeTicket(raw);
  },
};

// ========================
// Knowledge Base API
// ========================

export const knowledgeApi = {
  getCategories: async (): Promise<KnowledgeCategory[]> => {
    const resp = await apiClient.get('/kb/categories');
    const raw = unwrap(resp);
    // May be array directly or { items: [...] }
    if (Array.isArray(raw)) return raw.map(normalizeKnowledgeCategory);
    if (raw && raw.items) return raw.items.map(normalizeKnowledgeCategory);
    return [];
  },

  getArticles: async (params?: ArticlesListParams): Promise<PaginatedResponse<Article>> => {
    const queryParams: Record<string, unknown> = { ...params };
    if (params?.categoryId) queryParams.categoryId = params.categoryId;
    if (params?.search) queryParams.search = params.search;
    queryParams.type = 'public';
    queryParams.visibility = 'public';

    const resp = await apiClient.get('/kb/articles', { params: queryParams });
    const raw = unwrap(resp);
    const paginated = toPaginated<Article>(raw, params?.page || 1, params?.limit || 20);
    paginated.data = paginated.data.map(normalizeKnowledgeArticle);
    return paginated;
  },

  getArticleBySlug: async (slug: string): Promise<Article> => {
    // Backend uses /kb/articles/:id — slug can serve as lookup key
    const resp = await apiClient.get(`/kb/articles/${slug}`);
    return normalizeKnowledgeArticle(unwrap<Article>(resp));
  },

  markHelpful: async (id: string): Promise<{ helpfulCount: number }> => {
    const resp = await apiClient.post(`/kb/articles/${id}/helpful`);
    return unwrap<{ helpfulCount: number }>(resp);
  },

  createArticle: async (payload: CreateKnowledgeArticlePayload): Promise<Article> => {
    const resp = await apiClient.post('/kb/articles', payload);
    return normalizeKnowledgeArticle(unwrap<Article>(resp));
  },

  // Inline-редактирование статей с help.recore.su. Доступно админам/
  // менеджерам/девелоперам (роль чекается на бэке + в UI). Используются
  // те же эндпоинты, что и admin-app — /kb/articles/:id PATCH.
  updateArticle: async (id: string, payload: UpdateKnowledgeArticlePayload): Promise<Article> => {
    const resp = await apiClient.patch(`/kb/articles/${id}`, payload);
    return normalizeKnowledgeArticle(unwrap<Article>(resp));
  },

  uploadDemoMedia: async (file: File): Promise<UploadedKnowledgeMedia> => {
    const formData = new FormData();
    formData.append('file', file);

    const resp = await apiClient.post('/kb/demo-media', formData);
    return unwrap<UploadedKnowledgeMedia>(resp);
  },
};

// ========================
// Passport API («Паспорт клуба»)
// ========================

export interface PassportCredential {
  id?: string;
  label?: string;
  login?: string;
  password?: string;
  url?: string;
  note?: string;
}

export interface ClubPassport {
  id: string;
  clubId: string;
  clubName?: string;
  title: string;
  content: string;
  tags: string[];
  credentials: PassportCredential[];
  updatedAt: string;
}

export interface SavePassportPayload {
  clubId: string;
  title?: string;
  content?: string;
  tags?: string[];
  credentials?: PassportCredential[];
}

export const passportApi = {
  // Клубы текущего аккаунта (бэк сам скоупит /clubs на клиента).
  getMyClubs: async (): Promise<Club[]> => {
    const resp = await apiClient.get('/clubs');
    const raw = unwrap<any>(resp);
    const list = Array.isArray(raw) ? raw : raw?.data || raw?.items || [];
    return list.map((c: any) => ({
      id: c.id,
      name: c.name,
      address: c.address || '',
      deviceCount: c._count?.devices ?? c.deviceCount ?? 0,
    }));
  },

  get: async (clubId: string): Promise<ClubPassport | null> => {
    const resp = await apiClient.get('/kb/passport', { params: { clubId } });
    const raw = unwrap<any>(resp);
    if (!raw) return null;
    return {
      id: raw.id,
      clubId: raw.clubId,
      clubName: raw.clubName,
      title: raw.title || '',
      content: raw.content || '',
      tags: Array.isArray(raw.tags) ? raw.tags : [],
      credentials: Array.isArray(raw.credentials) ? raw.credentials : [],
      updatedAt: raw.updatedAt,
    };
  },

  save: async (payload: SavePassportPayload): Promise<ClubPassport> => {
    const resp = await apiClient.put('/kb/passport', payload);
    const raw = unwrap<any>(resp);
    return {
      id: raw.id,
      clubId: raw.clubId,
      clubName: raw.clubName,
      title: raw.title || '',
      content: raw.content || '',
      tags: Array.isArray(raw.tags) ? raw.tags : [],
      credentials: Array.isArray(raw.credentials) ? raw.credentials : [],
      updatedAt: raw.updatedAt,
    };
  },
};

// ========================
// Profile API
// ========================

export const profileApi = {
  getMyData: async (): Promise<User> => {
    const resp = await apiClient.get('/auth/me');
    return normalizeAuthUser(unwrap<any>(resp));
  },

  getClientData: async (): Promise<ClientProfile> => {
    // First get user info to know clientId
    const meResp = await apiClient.get('/auth/me');
    const me = normalizeAuthUser(unwrap<any>(meResp));
    const clientId = me.clientId;

    if (!clientId) {
      throw new Error('Client account is not linked to the current user.');
    }

    // Fetch client data
    const clientResp = await apiClient.get(`/clients/${clientId}`);
    const clientRaw = unwrap<any>(clientResp);

    // Fetch clubs
    const clubsResp = await apiClient.get('/clubs', { params: { clientId } });
    const clubsRaw = unwrap(clubsResp);
    const clubsList: Club[] = Array.isArray(clubsRaw)
      ? clubsRaw
      : clubsRaw?.items || [];

    // Fetch devices for each club
    const clubsWithDevices: Club[] = await Promise.all(
      clubsList.map(async (club: Club) => {
        try {
          const devResp = await apiClient.get('/devices', { params: { clubId: club.id } });
          const devRaw = unwrap(devResp);
          const devices: Device[] = Array.isArray(devRaw) ? devRaw : devRaw?.items || [];
          return {
            ...club,
            deviceCount: devices.length,
            devices,
          };
        } catch {
          return { ...club, devices: [] };
        }
      }),
    );

    return {
      ...normalizeClientProfile(clientRaw, clientId),
      clubs: clubsWithDevices,
    };
  },

  updateProfile: async (payload: UpdateProfilePayload): Promise<User> => {
    const meResp = await apiClient.get('/auth/me');
    const me = normalizeAuthUser(unwrap<any>(meResp));

    if (!me.clientId) {
      throw new Error('Client account is not linked to the current user.');
    }

    const resp = await apiClient.patch(`/clients/${me.clientId}`, {
      ...(payload.contactPerson !== undefined && { contactName: payload.contactPerson }),
      ...(payload.email !== undefined && { contactEmail: payload.email }),
      ...(payload.phone !== undefined && { contactPhone: payload.phone }),
    });

    const updatedClient = unwrap<any>(resp);
    return normalizeAuthUser({
      ...me,
      ...updatedClient,
      clientId: me.clientId,
    });
  },
};

// ===== TRAINING =====
export type TrainingDemoAction =
  | { type: 'move'; to: [number, number]; duration?: number }
  | { type: 'click'; at: [number, number] }
  | { type: 'wait'; duration: number }
  | { type: 'type'; at: [number, number]; text: string }
  | { type: 'highlight'; box: [number, number, number, number]; duration?: number }
  | { type: 'zoom'; box: [number, number, number, number]; duration?: number }
  | { type: 'comment'; at: [number, number]; text: string; duration?: number | null }
  | {
      type: 'hotspot';
      box: [number, number, number, number];
      inputText?: string | null;
      waitForUser?: boolean;
      locator?: TrainingLocator | null;
      title?: string;
    }
  | {
      // Drag gesture: hold mouse at `from`, drag to `to`, release.
      // Used to record drag-and-drop training actions (reorder lists,
      // drag-upload files, slider grips, etc.). Playback dispatches
      // mousedown/pointerdown at `from`, animated mousemove to `to`,
      // then mouseup/pointerup at `to`. `duration` = animation time ms.
      type: 'drag';
      from: [number, number];
      to: [number, number];
      duration?: number;
    };

export interface TrainingHotspot {
  box: [number, number, number, number];
  inputText?: string;
}

export interface TrainingComment {
  x: number;
  y: number;
  text: string;
}

export interface TrainingLocatorCandidate {
  kind: 'testid' | 'role-name' | 'text' | 'training-id' | 'bbox';
  value?: string;
  role?: string;
  name?: string;
  text?: string;
  bbox?: { x: number; y: number; w: number; h: number };
  scope?: { tag: string; label: string } | null;
}

export interface TrainingLocator {
  route: string;
  scope?: { tag: string; label: string } | null;
  candidates: TrainingLocatorCandidate[];
  diagnostics?: {
    tag: string;
    text: string;
    role: string;
    bbox: { x: number; y: number; w: number; h: number };
    viewport: { w: number; h: number };
  };
}

export interface TrainingStage {
  id: string;
  parentId: string | null;
  order: number;
  title: string;
  description: string | null;
  imageUrl: string;
  iframeUrl: string | null;
  audience?: 'admin' | 'director' | 'both';
  actions: TrainingDemoAction[];
  hotspot: TrainingHotspot | null;
  locator?: TrainingLocator | null;
  expectedInput?: string | null;
  comments?: TrainingComment[];
  createdAt: string;
  updatedAt: string;
}

export interface TrainingProgressEntry {
  stageId: string;
  completedAt: string;
}

export const trainingApi = {
  listStages: async (): Promise<TrainingStage[]> => {
    const { data } = await apiClient.get('/training/stages');
    return data.data;
  },
  getMyProgress: async (): Promise<TrainingProgressEntry[]> => {
    const { data } = await apiClient.get('/training/progress');
    return data.data;
  },
  markComplete: async (stageId: string): Promise<void> => {
    await apiClient.post(`/training/stages/${stageId}/complete`);
  },
  resetProgress: async (): Promise<void> => {
    await apiClient.delete('/training/progress');
  },
};

// Контакты для страницы «Контакты» — задаются в админке (Настройки → Контакты).
export interface ContactsSettings {
  email: string;
  emailNote: string;
  phone: string;
  phoneNote: string;
  hours: string;
  hoursNote: string;
  telegram: string;
  telegramNote: string;
  vk: string;
  vkUrl: string;
  vkNote: string;
  // Удалены из UI (горячая линия и адрес офиса), бэкенд ещё может их отдавать.
  hotlinePhone?: string;
  hotlineNote?: string;
  addressCity?: string;
  addressLine?: string;
  addressNote?: string;
}

export const settingsApi = {
  getContacts: async (): Promise<ContactsSettings> => {
    const { data } = await apiClient.get('/settings/contacts');
    return data;
  },
};
